import React, { useContext } from 'react'
import { useGlobals } from 'context'
import { Trans } from '@lingui/macro'
import { useQuery, useMutation } from '@apollo/client'
import { GET_LOCATIONS } from '../queries'
import { CHANGE_LOCATION, REMOVE_LOCATION } from '../mutations'

const LocationContext = React.createContext()

function LocationProvider({ children }) {
  const { createError, createSuccess } = useGlobals()
  const { loading, data, refetch } = useQuery(GET_LOCATIONS)
  const { locations } = data || {}
  const newUpdate = (cache, { data: { changeLocation } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_LOCATIONS,
      })
      cache.writeQuery({
        query: GET_LOCATIONS,
        data: { locations: [...cached.locations, changeLocation] },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const changeUpdate = (cache, { data: { changeLocation } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_LOCATIONS,
      })
      cache.writeQuery({
        query: GET_LOCATIONS,
        data: {
          locations: cached.locations.map((location) => {
            if (location.location_id === changeLocation.location_id) {
              return changeLocation
            }
            return location
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const removeUpdate = (cache, { data: { removeLocation } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_LOCATIONS,
      })
      cache.writeQuery({
        query: GET_LOCATIONS,
        data: {
          locations: cached.locations.filter(
            (location) => location.location_id !== removeLocation.location_id,
          ),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const [newLocation] = useMutation(CHANGE_LOCATION, {
    update: newUpdate,
  })
  const [updateLocation] = useMutation(CHANGE_LOCATION, {
    update: changeUpdate,
  })
  const [removeLocation] = useMutation(REMOVE_LOCATION, {
    update: removeUpdate,
  })

  async function create(input) {
    try {
      await newLocation({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Location added</Trans>)
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to add address</Trans>,
      )
    }
  }

  async function update(input) {
    try {
      await updateLocation({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Address updated</Trans>)
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to update address</Trans>,
      )
    }
  }

  async function remove(location_id) {
    try {
      await removeLocation({
        variables: {
          location_id,
        },
      })
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to remove location</Trans>,
      )
    }
  }

  return (
    <LocationContext.Provider
      value={{
        loading,
        refetch,
        locations,
        create,
        update,
        remove,
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}

function useLocations() {
  return useContext(LocationContext)
}

export { LocationProvider, useLocations }
